import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { Importer } from "components/Importer";
import { ImportDropdown } from "components/Importer/ImportDropdown";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { LepcSearchQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import compact from "lodash-es/compact";
import { useMemo, useState } from "react";

import { LEPC_SEARCH } from "./api";
import { ConfirmDeleteLEPC } from "./ConfirmDeleteLEPC";
import { CreateEditLepcDialog } from "./CreateEditDialog";

type Row = LepcSearchQuery["lepcSearch"]["items"][number];

type LepcsProps = {
  initialPageSize?: number;
};

export const Lepcs: React.FC<LepcsProps> = ({ initialPageSize }) => {
  const { selectedId: editLepcId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("lepcId");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDeleteLepc, setConfirmDeleteLepc] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 100 },
      { field: "name", headerName: "Name", flex: 3 },
      {
        field: "mailingAddress",
        headerName: "Mailing Address",
        flex: 2,
        valueGetter: (_, row) => {
          return compact([row.mailingAddress, row.mailingAddressLine2]).join(", ");
        },
      },
      { field: "city", headerName: "City", flex: 1 },
      { field: "county", headerName: "County", flex: 1 },
      { field: "state", headerName: "State", width: 75 },
      { field: "zip", headerName: "Zip", width: 75 },
      { field: "email", headerName: "Email", flex: 3 },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.3,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row: { issues } }) => {
          return <IssueCount issueCount={issues?.length} />;
        },
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        align: "right",
        getActions: ({ row }) => {
          return [
            <Tooltip title="Edit LEPC" key={1}>
              <GridActionsCellItem
                label="Edit LEPC"
                onClick={() => pushIdToUrl(row.id)}
                icon={<Edit />}
              />
            </Tooltip>,
            <Tooltip title="Delete LEPC" key={2}>
              <GridActionsCellItem
                label="Delete LEPC"
                onClick={() => setConfirmDeleteLepc(row)}
                icon={<Delete />}
              />
            </Tooltip>,
          ];
        },
      },
    ],
    [pushIdToUrl]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="lepcs"
        isURLDriven
        columns={columns}
        dataQuery={LEPC_SEARCH}
        getItems={(data: any) => data.lepcSearch.items ?? []}
        getCount={(data: any) => data.lepcSearch.count ?? []}
        noDataMessage="No LEPCs found."
        initialPageSize={initialPageSize ?? 25}
        onRowClick={({ row }) => {
          pushIdToUrl(row.id);
        }}
        commandButtons={[
          <ImportDropdown
            key="import"
            importers={[
              {
                name: "LEPCs",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.LEPC}
                    refetchQueries={["LepcSearch"]}
                  />
                ),
              },
            ]}
          />,
          <ExportDataButton key="export" exportType="lepcs" fileName="lepcs" />,
          <Button key="add-lepc-button" variant="contained" onClick={() => setAddDialogOpen(true)}>
            Add LEPC
          </Button>,
        ]}
      />
      {editLepcId && (
        <CreateEditLepcDialog onClose={popIdFromUrl} mode="edit" lepcId={editLepcId} />
      )}
      {addDialogOpen && <CreateEditLepcDialog onClose={() => setAddDialogOpen(false)} mode="add" />}

      {confirmDeleteLepc && (
        <ConfirmDeleteLEPC
          lepcId={confirmDeleteLepc.id}
          onClose={() => setConfirmDeleteLepc(null)}
        />
      )}
    </Box>
  );
};
